//

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { FormikProps } from "formik/dist/types";
import { useRouter } from "next/router";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import KeywordSuggestionsDropdown from "src/components/common/KeywordSuggestionsDropdown";
import InsightsEditor from "src/components/dashboard/brief/rightSection/InsightsEditor";
import { MagicWandIcon } from "src/icons/magic-wand-icon";
import { getActivePlan } from "src/slices/auth/thunks";
import { languages, locations, usStates } from "src/slices/brief/helpers";
import { clearSuggestions } from "src/slices/keywordSuggestions";
import {
  selectKeywordSuggestions,
  selectKeywordSuggestionsError,
  selectKeywordSuggestionsLoading,
} from "src/slices/keywordSuggestions/selectors";
import { fetchKeywordSuggestions } from "src/slices/keywordSuggestions/thunks";
import { getProjects, projects } from "src/slices/projects";
import { checkKeywordRanking } from "src/slices/system/thunks";
import { useDispatch, useSelector } from "src/store";
import KeywordRankingChip from "./KeywordRankingChip";
import SuccessConfirmation from "./SuccessConfirmation";

const filter = createFilterOptions();
interface OptimizeContentData {
  keyword: string;
  articleLink: string;
  location: (typeof locations)[0];
  state: (typeof usStates)[0] | null;
  language: string;
  project: any;
  stats: boolean;
  insights: string;
}

type Props = {
  formik: FormikProps<{
    keyword: string;
    location: (typeof locations)[0];
    state: (typeof usStates)[0] | null;
    language: string;
    project: {
      projectName: string;
      projectId: string;
      projectDomain: string;
      isGSCIntegrated: boolean;
      companyDetails: any;
      GSCsiteUrl: string;
    };
    generateArticle: boolean;
    interlinking: boolean;
    stats: boolean;
    thoughtLeadership: boolean;
    insights: string;
    generateAutomatedInsights: boolean;
    companyDetails: boolean;
    humanize: boolean;
    aiResearchAgent: boolean;
    aiEvalRefineAgent: boolean;
  }>;
  onOptimizeContent?: (data: OptimizeContentData) => void;
};

const ChooseKeyword = ({ formik, onOptimizeContent }: Props) => {
  const router = useRouter();

  const [currentPlan, setCurrentPlan] = useState<any>({});
  const [isInterLinkingAdded, setIsInterLinkingAdded] = useState(false);
  const [isCompanyDetailAdded, setIsCompanyDetailAdded] = useState(false);
  const [isHumanizeAvailableAtPlan] = useState(true);
  const [humanizeToggletext] = useState(
    `Once your article is generated, our system proofreads, rewrites, and adjusts the text using AI to sound more natural and human-like, helping it avoid AI detection.
    <br /><br />
    Note: Headings and structure may change to improve readability. This is an automated process.`
  );
  // const currentPlan: any = useSelector(selectCurrentPlan);
  const Project = formik.values.project;

  let Projects = useSelector(projects);
  const updatedProjects = [
    ...Projects,
    {
      projectId: "new",
      projectName: "Add new project",
      projectDomain: "",
      isGSCIntegrated: false,
      GSCsiteUrl: "",
      createdAt: "",
      updatedAt: "",
      briefCount: 0,
      briefs: [],
      AImodel: "claude",
      excludedLinks: [],
      brandVoice: "",
      companyDetails: {
        companyName: "",
        companyInformation: "",
        productInformation: "",
        targetAudience: "",
      },
      isSeoMissionCreated: false,
      competitors: [],
      optimizedArticleCount: 0,
    },
  ];

  const getCurrentPlan = async () => {
    const currentPlan = (await dispatch(getActivePlan())) as any;
    setCurrentPlan(currentPlan?.payload?.plan);
  };
  useEffect(() => {
    getCurrentPlan();
  }, []);

  const [insights, setInsights] = useState(false);

  // Keyword ranking check state
  const [keywordRankingData, setKeywordRankingData] = useState<any>(null);
  const [checkingRanking, setCheckingRanking] = useState(false);
  const [showRankingAlert, setShowRankingAlert] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>("");
  const [lastCheckedComposite, setLastCheckedComposite] = useState<string>("");
  const [rankingError, setRankingError] = useState<string>("");

  // Keyword suggestions state
  const keywordInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const keywordSuggestions = useSelector(selectKeywordSuggestions);
  const suggestionsLoading = useSelector(selectKeywordSuggestionsLoading);
  const suggestionsError = useSelector(selectKeywordSuggestionsError);

  const [interLinkingToggletext, setInterLinkingToggletext] = useState("");

  const [aiResearchAgentToggletext, setAiResearchAgentToggletext] = useState(
    "Enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible."
  );
  const [placeholderText, setPlaceholderText] = useState(
    "Add your insights here..."
  );
  const [aiEvalRefineAgentToggletext, setAiEvalRefineAgentToggletext] =
    useState(
      "Enable AI-powered evaluation to improve your AI-generated article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
    );
  const isInterLinkingAvailableAtPlan =
    currentPlan?.interlinking === "true" || currentPlan?.interlinking === true;

  const isAiResearchAgentAvailableAtPlan =
    currentPlan?.aiResearchAgent === true ||
    currentPlan?.aiResearchAgent === "true";
  const isAiEvalRefineAgentAvailableAtPlan =
    currentPlan?.aiEvalAgent === true || currentPlan?.aiEvalAgent === "true";
  const handleInsightsAdded = (event: ChangeEvent<any>) => {
    setInsights(event.target.checked);
    formik.setFieldValue("generateAutomatedInsights", event.target.checked);

    formik.setFieldValue("brief.insights", "");
  };

  useEffect(() => {
    if (formik.values?.insights) {
      setInsights(true);
      formik.setFieldValue("insights", formik.values.insights);
      formik.setFieldValue("generateAutomatedInsights", false);
      // event.target.checked
    }
  }, [formik.values.insights]);

  const handleLocationChange = (event: ChangeEvent<any>, value: any) => {
    formik.setFieldValue("location", value);
    localStorage.setItem("brief-location", JSON.stringify(value));

    // Reset state when location changes and it's not US
    if (value?.location_name !== "United States") {
      formik.setFieldValue("state", null);
      localStorage.removeItem("brief-state");
    }
  };

  const handleStateChange = (event: ChangeEvent<any>, value: any) => {
    formik.setFieldValue("state", value);
    if (value) {
      localStorage.setItem("brief-state", JSON.stringify(value));
    } else {
      localStorage.removeItem("brief-state");
    }
  };

  const handleLanguageChange = (event: ChangeEvent<any>) => {
    formik.handleChange(event);
    localStorage.setItem("brief-language", event.target.value);
  };

  /* projects commented out */
  const handleProjectChange = (event: ChangeEvent<any>, value) => {
    if (value?.projectId === "new") {
      handleNewProject(event as any);
      return;
    }
    formik.setFieldValue("project", value);
    localStorage.setItem("brief-project", JSON.stringify(value));
  };

  const handleNewProject = (event: React.MouseEvent) => {
    event.stopPropagation();
    router.push({
      pathname: "/projects/settings/",
      query: {
        url: "new",
      },
    });
  };

  const dispatch = useDispatch();
  const redirectToProject = () => {
    if (Project?.projectId) {
      return `/app/projects/settings?url=edit&id=${Project?.projectId}`;
    }
    return "/app/projects";
  };

  const returnHTML = () => {
    let html =
      "We will include information about your company in the AI article if it's relevant for the brief. To add information about your company go to <a style='color: #eef3fd; ' href=" +
      redirectToProject() +
      "> " +
      `${Project?.projectName}` +
      "</a> Project Settings.";
    return html;
  };

  useEffect(() => {
    if (Project?.companyDetails) {
      setIsCompanyDetailAdded(true);
      formik.setFieldValue("companyDetails", true);
    } else {
      formik.setFieldValue("companyDetails", false);
      setIsCompanyDetailAdded(false);
    }
    if (
      formik.values?.project?.isGSCIntegrated &&
      isInterLinkingAvailableAtPlan
    ) {
      setIsInterLinkingAdded(true);
      formik.setFieldValue("interlinking", true);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. To integrate with your GSC go to <a style='color: #eef3fd; ' href=" +
          redirectToProject() +
          "> " +
          `${formik.values?.project?.projectName}` +
          "</a> Project Settings."
      );
    } else if (
      !formik.values?.project?.isGSCIntegrated &&
      isInterLinkingAvailableAtPlan
    ) {
      setIsInterLinkingAdded(false);
      formik.setFieldValue("interlinking", false);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. To integrate with your GSC go to <a style='color: #eef3fd; ' href=" +
          redirectToProject() +
          "> " +
          `${formik.values?.project?.projectName}` +
          "</a> Project Settings."
      );
    } else {
      setIsInterLinkingAdded(false);
      formik.setFieldValue("interlinking", false);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. Upgrade your plan to unlock the feature."
      );
    }
  }, [formik.values.project, isInterLinkingAvailableAtPlan]);

  useEffect(() => {
    if (isAiResearchAgentAvailableAtPlan) {
      formik.setFieldValue("aiResearchAgent", true);
      setAiResearchAgentToggletext(
        "Enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible."
      );
    } else {
      setAiResearchAgentToggletext(
        "Upgrade to a higher plan to enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible."
      );
      formik.setFieldValue("aiResearchAgent", false);
    }
    if (isAiEvalRefineAgentAvailableAtPlan) {
      formik.setFieldValue("aiEvalRefineAgent", true);
      setAiEvalRefineAgentToggletext(
        "Enable AI-powered evaluation to improve your AI-generated article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
      );
    } else {
      setAiEvalRefineAgentToggletext(
        "Upgrade to a higher plan and enable AI-powered evaluation to improve your AI-generated article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
      );
      formik.setFieldValue("aiEvalRefineAgent", false);
    }
  }, [currentPlan]);
  useEffect(() => {
    dispatch(getProjects());
  }, []);

  // Debounced keyword ranking check
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkRanking = async () => {
      const keyword = formik.values.keyword?.trim();
      const project = formik.values.project;
      const compositeKey = `${keyword || "no-keyword"}-${
        project?.projectId || "no-project"
      }`;

      // Reset previous state
      setShowRankingAlert(false);
      setShowSuccessMessage(false);
      setKeywordRankingData(null);
      setRankingError("");
      setLastCheckedComposite(compositeKey);

      // Check conditions one by one with debugging
      if (!keyword) {
        setDebugInfo("No keyword entered");

        return;
      }

      if (keyword.length <= 2) {
        setDebugInfo(
          `Keyword too short (${keyword.length} chars). Need 3+ characters.`
        );
        return;
      }

      if (!project) {
        setDebugInfo("No project selected");

        return;
      }

      if (!project.isGSCIntegrated) {
        setDebugInfo("Project doesn't have GSC integration enabled");

        return;
      }

      if (!project.GSCsiteUrl) {
        setDebugInfo("Project missing GSC site URL");

        return;
      }

      // All conditions met, proceed with API call

      setDebugInfo("Checking keyword rankings...");
      setCheckingRanking(true);

      try {
        const response: any = await dispatch(
          checkKeywordRanking({
            keyword,
            GSCsiteUrl: project.GSCsiteUrl,
            projectId: project.projectId,
          })
        );

        if (response?.payload?.success) {
          if (response.payload.isRanking) {
            setKeywordRankingData(response.payload.data);
            setShowRankingAlert(true);
            setDebugInfo(
              `Found ranking at position ${response.payload.data.position}`
            );
          } else {
            setDebugInfo("No ranking found in top 10");
            setShowSuccessMessage(true);

            // Auto-dismiss success message after 4 seconds
            setTimeout(() => {
              setShowSuccessMessage(false);
            }, 4000);
          }
        } else {
          const errorMessage = response?.payload?.error || "Unknown API error";
          setRankingError(errorMessage);
          setDebugInfo(`API Error: ${errorMessage}`);
          setShowRankingAlert(true);
        }
      } catch (error) {
        console.error("💥 Error checking keyword ranking:", error);
        const errorMessage = `Error: ${String(error)}`;
        setRankingError(errorMessage);
        setDebugInfo(errorMessage);
        setShowRankingAlert(true);
      } finally {
        setCheckingRanking(false);
      }
    };

    // Clear debug info when keyword or project changes
    setDebugInfo("");

    // Create current composite key for comparison
    const currentComposite = `${formik.values.keyword || "no-keyword"}-${
      formik.values.project?.projectId || "no-project"
    }`;

    // Debounce the API call by 500ms - trigger when keyword OR project changes
    if (formik.values.keyword && currentComposite !== lastCheckedComposite) {
      timeoutId = setTimeout(checkRanking, 500);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    formik.values.keyword,
    formik.values.project,
    dispatch,
    lastCheckedComposite,
  ]);

  const handleDismissRankingAlert = () => {
    setShowRankingAlert(false);
    setShowSuccessMessage(false);
    setKeywordRankingData(null);
    setRankingError("");
    setDebugInfo("");
  };

  const handleOptimizeContent = () => {
    if (!keywordRankingData) return;

    // If onOptimizeContent prop is provided, use the new integration
    if (onOptimizeContent) {
      // Prepare data for OptimizeContentDialog
      const optimizeData: OptimizeContentData = {
        keyword: keywordRankingData.keyword,
        articleLink: keywordRankingData.url,
        location: formik.values.location,
        state: formik.values.state,
        language: formik.values.language,
        project: formik.values.project,
        stats: false,
        insights: "",
      };

      // Pass data to parent component to handle dialog transition
      onOptimizeContent(optimizeData);
    } else {
      // Fallback behavior: Open URL in new tab and show confirmation

      const action = window.confirm(
        `You're ranking #${keywordRankingData.position} for "${keywordRankingData.keyword}"\n\n` +
          `Current page: ${keywordRankingData.url}\n` +
          `Performance: ${keywordRankingData.clicks} clicks, ${keywordRankingData.impressions} impressions (${keywordRankingData.ctr}% CTR)\n\n` +
          `Click OK to open the ranking page for optimization, or Cancel to dismiss.`
      );

      if (action) {
        // Open the ranking page for optimization
        window.open(keywordRankingData.url, "_blank", "noopener,noreferrer");
      }
    }

    // Dismiss the alert after action
    handleDismissRankingAlert();
  };

  // Keyword suggestions handlers
  const handleSuggestionsClick = () => {
    const keyword = formik.values.keyword?.trim();
    if (!keyword || keyword.length < 2) {
      return;
    }

    const locationName =
      formik.values.location?.location_name || "United States";
    const languageCode = formik.values.language || "English";

    // Convert language name to code
    const languageObj = languages.find(
      (lang) => lang.languageName === languageCode
    );
    const languageCodeForAPI = languageObj?.languageCode || "en";

    dispatch(
      fetchKeywordSuggestions({
        keyword,
        locationName,
        languageCode: languageCodeForAPI,
      })
    );
    setSuggestionsOpen(true);
  };

  const handleSuggestionSelect = (selectedKeyword: string) => {
    formik.setFieldValue("keyword", selectedKeyword);

    setSuggestionsOpen(false);
    dispatch(clearSuggestions());
  };

  const handleSuggestionsClose = () => {
    setSuggestionsOpen(false);
    dispatch(clearSuggestions());
  };

  // Handle manual typing in the keyword field
  const handleKeywordChange = (event: ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    // Close suggestions when user manually types
    if (suggestionsOpen) {
      handleSuggestionsClose();
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsOpen) {
        const target = event.target as Node;
        const isClickInInput = keywordInputRef.current?.contains(target);
        const isClickInDropdown = dropdownRef.current?.contains(target);

        // Only close if click is outside both input and dropdown
        if (!isClickInInput && !isClickInDropdown) {
          handleSuggestionsClose();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [suggestionsOpen]);

  // Note: Suggestions close manually when user types or clicks outside

  // Development testing functions
  const simulateRankingFound = () => {
    const mockData = {
      keyword: formik.values.keyword || "test keyword",
      url: "https://example.com/test-page",
      position: 3.2,
      clicks: 150,
      impressions: 2500,
      ctr: 6.0,
    };
    setKeywordRankingData(mockData);
    setShowRankingAlert(true);
    setRankingError("");
    setDebugInfo("Mock data: Simulated ranking found");
  };

  const simulateError = () => {
    setRankingError("Test error: Unable to fetch GSC data");
    setKeywordRankingData(null);
    setShowRankingAlert(true);
    setDebugInfo("Mock data: Simulated API error");
  };

  const simulateNoRanking = () => {
    setRankingError("");
    setKeywordRankingData(null);
    setShowRankingAlert(false);
    setDebugInfo("Mock data: No ranking found");
  };

  // console.log("Projects in keywords", Projects);
  const handleCheckBoxChange = (event: ChangeEvent<any>) => {
    formik.setFieldValue("generateArticle", event.target.checked);
    localStorage.setItem("brief-generateArticle", event.target.checked);
  };
  return (
    <Box sx={{ p: 3, pt: 4 }}>
      <Typography align="center" gutterBottom variant="h5">
        Let’s start with the Brief
      </Typography>
      <Typography align="center" gutterBottom variant="body1">
        Enter one keyword you would like to create content for and choose the
        location.
      </Typography>
      <Box sx={{ pt: 3 }}>
        <TextField
          fullWidth
          label="Focus keyword"
          name="keyword"
          type="text"
          value={formik.values.keyword}
          defaultValue={formik.values.keyword}
          onBlur={formik.handleBlur}
          onChange={handleKeywordChange}
          inputRef={keywordInputRef}
          helperText={
            formik.errors.keyword && formik.touched
              ? formik.errors.keyword
              : undefined
          }
          sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={
                    !formik.values.keyword?.trim()
                      ? "Enter a keyword to get suggestions"
                      : formik.values.keyword.length < 2
                      ? "Keyword must be at least 2 characters"
                      : suggestionsLoading
                      ? "Loading suggestions..."
                      : "Get keyword suggestions"
                  }
                >
                  <span>
                    <IconButton
                      onClick={handleSuggestionsClick}
                      disabled={
                        !formik.values.keyword?.trim() ||
                        formik.values.keyword.length < 2 ||
                        suggestionsLoading
                      }
                      sx={{
                        color: "#5048E5",
                        "&:hover": {
                          backgroundColor: "rgba(80, 72, 229, 0.1)",
                        },
                        "&:disabled": {
                          color: "#ccc",
                        },
                      }}
                    >
                      {suggestionsLoading ? (
                        <CircularProgress size={20} sx={{ color: "#5048E5" }} />
                      ) : (
                        <LightbulbIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />

        {/* Keyword Suggestions Dropdown */}
        <KeywordSuggestionsDropdown
          anchorEl={keywordInputRef.current}
          open={suggestionsOpen}
          suggestions={keywordSuggestions}
          loading={suggestionsLoading}
          error={suggestionsError}
          onSuggestionSelect={handleSuggestionSelect}
          onClose={handleSuggestionsClose}
          dropdownRef={dropdownRef}
        />

        {/* Compact Ranking Accordion */}
        {showRankingAlert && keywordRankingData && !rankingError && (
          <KeywordRankingChip
            rankingData={keywordRankingData}
            onOptimizeContent={handleOptimizeContent}
          />
        )}

        {/* Success Confirmation - No ranking found */}
        {showSuccessMessage && (
          <SuccessConfirmation keyword={formik.values.keyword} />
        )}

        {/* Error State - Keep minimal */}
        {showRankingAlert && rankingError && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="error"
              sx={{ fontSize: "0.75rem" }}
            >
              ⚠️ {rankingError}
            </Typography>
          </Box>
        )}

        {/* Enhanced Loading State */}
        {checkingRanking && (
          <Box
            sx={{
              mt: 2,
              mb: 1,
              p: 2,
              border: "1px solid rgba(80, 72, 229, 0.2)",
              borderRadius: 1,
              backgroundColor: "rgba(80, 72, 229, 0.05)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minHeight: "56px",
            }}
          >
            <CircularProgress size={20} sx={{ color: "#5048E5" }} />
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: "#5048E5",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  lineHeight: 1.2,
                }}
              >
                🔍 Analyzing your search rankings
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#6b7280",
                  fontSize: "0.75rem",
                  display: "block",
                }}
              >
                Checking if you already rank for this keyword...
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Grid container gap={1} sx={{ pt: 3 }}>
        <Grid>
          <FormControl fullWidth>
            <Autocomplete
              id="location"
              options={locations}
              getOptionLabel={(option: {
                location_name: string;
                country_iso_code: string;
              }) => option.location_name}
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location"
                  helperText={
                    formik.errors.location && formik.touched
                      ? formik.errors.location
                      : undefined
                  }
                  sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
                />
              )}
              value={formik.values.location}
              onChange={handleLocationChange}
              renderOption={(props, option) => (
                <MenuItem
                  key={option.location_name}
                  value={option.location_name}
                  {...props}
                >
                  {option.location_name}
                </MenuItem>
              )}
            />
          </FormControl>
        </Grid>
        <Grid flex={1}>
          <FormControl fullWidth>
            <InputLabel id="language-label">Language</InputLabel>
            <Select
              labelId="language-label"
              id="language"
              value={formik.values.language}
              label="language"
              name="language"
              onChange={handleLanguageChange}
              defaultValue="English"
            >
              {languages
                .sort((a, b) => {
                  if (a.languageName > b.languageName) {
                    return 1;
                  } else if (a.languageName < b.languageName) {
                    return -1;
                  } else {
                    return 0;
                  }
                })
                .map((value) => (
                  <MenuItem key={value.languageName} value={value.languageName}>
                    {value.languageName === "Arabic" ||
                    value.languageName === "Hebrew"
                      ? value.languageName + " " + "(Beta)"
                      : value.languageName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* US State Dropdown - appears below when United States is selected */}
      <Collapse
        in={formik.values.location?.location_name === "United States"}
        timeout={300}
      >
        <Grid container gap={1} sx={{ pt: 3 }}>
          <Grid>
            <FormControl fullWidth>
              <Autocomplete
                id="state"
                options={usStates}
                getOptionLabel={(option: (typeof usStates)[0]) =>
                  option.state_name
                }
                isOptionEqualToValue={(option, value) =>
                  option.state_code === value?.state_code
                }
                PopperComponent={(props) => (
                  <Popper {...props} placement="bottom-start" />
                )}
                sx={{
                  width: 300,
                  "& .MuiOutlinedInput-root": {
                    boxShadow:
                      formik.values.location?.location_name === "United States"
                        ? "0 0 0 2px rgba(80, 72, 229, 0.1)"
                        : "none",
                    transition: "box-shadow 0.3s ease-in-out",
                  },
                }}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State (Optional)"
                    placeholder="Select a state for more targeted results"
                    helperText={
                      formik.errors.state && formik.touched.state
                        ? formik.errors.state
                        : "Leave blank for country-wide targeting"
                    }
                    sx={{
                      "& .MuiFormHelperText-root": {
                        color:
                          formik.errors.state && formik.touched.state
                            ? "#D14343"
                            : "#7B85AD",
                      },
                    }}
                  />
                )}
                value={formik.values.state}
                onChange={handleStateChange}
                renderOption={(props, option) => (
                  <MenuItem
                    key={option.state_code}
                    value={option.state_name}
                    {...props}
                  >
                    {option.state_name}
                  </MenuItem>
                )}
                clearOnEscape
                disableClearable={false}
              />
            </FormControl>
          </Grid>
          <Grid flex={1}>
            <Box />
          </Grid>
        </Grid>
      </Collapse>

      {/* projects commented out */}
      <Box sx={{ pt: 3 }}>
        <FormControl fullWidth>
          <Autocomplete
            id="project"
            options={updatedProjects}
            getOptionLabel={(option: {
              projectName: string;
              projectDomain: string;
              projectId: string;
            }) =>
              // Value selected with enter, right from the input
              option.projectName
            }
            sx={{ width: 552 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Choose a project"
                helperText={
                  formik.errors.project && formik.touched.project
                    ? formik.errors.project
                    : undefined
                }
                sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
              />
            )}
            value={formik.values.project}
            onChange={handleProjectChange}
            filterOptions={(options, params) => {
              let filtered = filter(options, params);
              if (params.inputValue !== "") {
                //show projecy with projectId new
                filtered.push(updatedProjects[updatedProjects.length - 1]);
              }
              return filtered;
            }}
            selectOnFocus
            freeSolo
            renderOption={(props, option) => (
              <>
                {option.projectId === "new" ? (
                  <MenuItem
                    key={option.projectId}
                    value={option.projectId}
                    {...props}
                  >
                    <Box
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      sx={{
                        width: "100%",
                        // paddingLeft: "1rem",
                      }}
                    >
                      <Button
                        variant="outlined"
                        sx={{
                          fontSize: "11px",
                          fontWeight: "600",
                          fontFamily: "Inter",
                          color: "#5048E5",
                          float: "left",
                          marginTop: "0.5rem",
                          // marginBottom: "1.5rem",
                          borderRadius: "4px",
                          padding: "8px",
                        }}
                        onClick={(event) => {
                          handleNewProject(event);
                        }}
                      >
                        <span style={{ marginRight: "0.5rem" }}>+</span>
                        Add New Project
                      </Button>
                    </Box>
                  </MenuItem>
                ) : (
                  <MenuItem
                    key={option.projectId}
                    value={option.projectId}
                    {...props}
                  >
                    {option.projectName}
                  </MenuItem>
                )}
              </>
            )}
          />
        </FormControl>
      </Box>

      {/* GSC Integration Status Display */}
      {formik.values.project && (
        <Box sx={{ mt: 1, mb: 1 }}>
          {formik.values.project.isGSCIntegrated ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label="GSC Integrated"
                color="success"
                size="small"
                variant="outlined"
                sx={{ fontSize: "11px" }}
              />
              <Typography variant="caption" color="text.secondary">
                Keyword ranking checks enabled for{" "}
                {formik.values.project.GSCsiteUrl}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label="No GSC Integration"
                color="default"
                size="small"
                variant="outlined"
                sx={{ fontSize: "11px" }}
              />
              <Typography variant="caption" color="text.secondary">
                Enable GSC integration in project settings to check keyword
                rankings
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1,
        }}
      >
        <Box sx={{ pt: 1, textAlign: "left" }}>
          <Box display={"flex"} alignItems={"center"}>
            <Checkbox
              name="generateArticle"
              onBlur={formik.handleBlur}
              onChange={handleCheckBoxChange}
              value={formik.values.generateArticle}
              checked={formik.values.generateArticle}
              sx={{
                ml: -1.5,
              }}
            />
            <InputLabel id="generate">Generate article with 1-click</InputLabel>
            <Tooltip
              title={
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      "SEOwind's advanced AI creates the ideal brief and writes the article to nail the search intent.",
                  }}
                />
              }
              placement="top"
            >
              <InfoIcon sx={{ fontSize: "16px", ml: "5px", mt: "2px" }} />
            </Tooltip>
          </Box>

          {formik.values.generateArticle && (
            <Box sx={{ pt: 1 }}>
              <Typography
                gutterBottom
                sx={{ fontWeight: 500, fontSize: "1.1rem", mr: 1 }}
              >
                Create article with:
              </Typography>
              {/* companyInfo toggle */}
              <Box display={"flex"} alignItems={"center"}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={formik.handleChange}
                        disabled={!isCompanyDetailAdded}
                        checked={formik.values.companyDetails}
                        name="companyDetails"
                        value={formik.values.companyDetails}

                        // defaultChecked={companyChecked}

                        // companyChecked
                      />
                    }
                    label="Company & Product Information"
                  />
                </FormGroup>
                {/* add tool tip  */}
                <Tooltip
                  title={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: returnHTML(),
                      }}
                    />
                  }
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
                </Tooltip>
              </Box>
              {/* AI agent */}
              <Box display={"flex"} alignItems={"center"}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={formik.handleChange}
                        disabled={!isAiResearchAgentAvailableAtPlan}
                        checked={formik.values?.aiResearchAgent}
                        // defaultChecked={interLinkingChecked}
                        name="aiResearchAgent"
                        value={formik.values?.aiResearchAgent}
                      />
                    }
                    label="AI Research Agent"
                  />
                </FormGroup>
                {/* add tool tip  */}
                <Tooltip
                  title={
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          aiResearchAgentToggletext ||
                          "Upgrade to a higher plan to enable AI-driven research to enhance your article with relevant statistics, expert insights, case studies, emerging trends, and even data from your own website. This feature ensures your content is backed by high-quality, authoritative information, making it more engaging and credible.",
                      }}
                    />
                  }
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
                </Tooltip>
              </Box>
              {/* a sublist of tasks for ai agent with icons check */}
              <Box
                display={"flex"}
                alignItems={"center"}
                sx={{
                  ml: "0.5rem",
                  mt: "-0.5rem",
                }}
              >
                <List sx={{ px: 0.3 }}>
                  <ListItem
                    sx={{
                      py: 0.3,

                      "& .MuiListItemText-root": {
                        my: 0,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {formik.values?.aiResearchAgent ? (
                        <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                      ) : (
                        <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Statistics & Quotes"
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      sx={{
                        ml: "-0.5rem",
                      }}
                    />
                  </ListItem>
                  <ListItem
                    sx={{
                      py: 0.3,

                      "& .MuiListItemText-root": {
                        my: 0,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {formik.values?.aiResearchAgent ? (
                        <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                      ) : (
                        <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Thought Leadership Insights"
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      sx={{
                        ml: "-0.5rem",
                      }}
                    />
                  </ListItem>
                  <ListItem
                    sx={{
                      py: 0.3,

                      "& .MuiListItemText-root": {
                        my: 0,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {formik.values?.aiResearchAgent ? (
                        <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                      ) : (
                        <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Case Studies"
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      sx={{
                        ml: "-0.5rem",
                      }}
                    />
                  </ListItem>
                  <ListItem
                    sx={{
                      py: 0.3,

                      "& .MuiListItemText-root": {
                        my: 0,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {formik.values?.aiResearchAgent ? (
                        <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                      ) : (
                        <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Emerging Trends"
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      sx={{
                        ml: "-0.5rem",
                      }}
                    />
                  </ListItem>
                  <ListItem
                    sx={{
                      py: 0.3,

                      "& .MuiListItemText-root": {
                        my: 0,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {formik.values?.companyDetails &&
                      formik.values?.aiResearchAgent ? (
                        <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                      ) : (
                        <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Insights from Your Website"
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      sx={{
                        ml: "-0.5rem",
                      }}
                    />
                  </ListItem>
                </List>
              </Box>
              {/* AI Eval & Refine Agent  */}
              <Box display={"flex"} alignItems={"center"}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={formik.handleChange}
                        disabled={!isAiEvalRefineAgentAvailableAtPlan}
                        checked={formik.values?.aiEvalRefineAgent}
                        // defaultChecked={interLinkingChecked}
                        name="aiEvalRefineAgent"
                        value={formik.values?.aiEvalRefineAgent}
                      />
                    }
                    label="AI Eval & Refine Agent"
                  />
                </FormGroup>
                {/* add tool tip  */}
                <Tooltip
                  title={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: aiEvalRefineAgentToggletext,
                      }}
                    />
                  }
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
                </Tooltip>
              </Box>
              {/* a sublist of tasks for ai eval & refine agent with icons check */}
              <Box
                display={"flex"}
                alignItems={"center"}
                sx={{
                  ml: "0.5rem",
                  mt: "-0.5rem",
                }}
              >
                <List sx={{ px: 0.3 }}>
                  <ListItem
                    sx={{
                      py: 0.3,
                      "& .MuiListItemText-root": {
                        my: 0,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {formik.values?.aiEvalRefineAgent ? (
                        <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                      ) : (
                        <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Reviews initial article and provides feedback"
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      sx={{
                        ml: "-0.5rem",
                      }}
                    />
                  </ListItem>
                  <ListItem
                    sx={{
                      py: 0.3,
                      "& .MuiListItemText-root": {
                        my: 0,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {formik.values?.aiEvalRefineAgent ? (
                        <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                      ) : (
                        <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Gathers additional data and insights"
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      sx={{
                        ml: "-0.5rem",
                      }}
                    />
                  </ListItem>
                  <ListItem
                    sx={{
                      py: 0.3,
                      "& .MuiListItemText-root": {
                        my: 0,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {formik.values?.aiEvalRefineAgent ? (
                        <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                      ) : (
                        <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="Rewrites & refines the final article for E-E-A-T"
                      primaryTypographyProps={{ fontSize: "0.875rem" }}
                      sx={{
                        ml: "-0.5rem",
                      }}
                    />
                  </ListItem>
                </List>
              </Box>

              {/* interlinking toggle */}
              <Box display={"flex"} alignItems={"center"}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={formik.handleChange}
                        // disabled={!isInterLinkingAdded}
                        checked={formik.values.interlinking}
                        // defaultChecked={interLinkingChecked}
                        name="interlinking"
                        value={formik.values.interlinking}
                        disabled={!isInterLinkingAdded}
                      />
                    }
                    label="Internal Linking"
                  />
                </FormGroup>
                {/* add tool tip  */}
                <Tooltip
                  title={
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          interLinkingToggletext ||
                          "We'll add internal links to your AI article if they match your outline.",
                      }}
                    />
                  }
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
                </Tooltip>
              </Box>

              {/* humanize toggle */}
              <Box display={"flex"} alignItems={"center"}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={formik.handleChange}
                        disabled={!isHumanizeAvailableAtPlan}
                        checked={formik.values.humanize}
                        name="humanize"
                        value={formik.values.humanize}
                      />
                    }
                    label="AI Humanizer & Proofreader"
                  />
                </FormGroup>
                {/* add tool tip  */}
                <Tooltip
                  title={
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          humanizeToggletext ||
                          "We'll add your personal experience, know-how, data and quotes to enrich the AI article.",
                      }}
                    />
                  }
                  placement="top"
                >
                  <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
                </Tooltip>
              </Box>

              <Box display={"flex"} alignItems={"center"}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={handleInsightsAdded}
                        // disabled={!isInsightsAdded}
                        checked={insights}
                        defaultChecked={insights}
                      />
                    }
                    label="Your insights and instructions"
                  />
                </FormGroup>
                {/* add tool tip  */}
                {/* <Tooltip
                title={
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        interLinkingToggletext ||
                        "We'll add internal links to your AI article if they match your outline.",
                    }}
                  />
                }
                placement="top"
              >
                <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
              </Tooltip> */}
              </Box>

              {insights && (
                <Box
                  // display={"flex"}
                  alignItems={"center"}
                  width="100%"
                  // height="170px"
                >
                  <Box width="100%" height="53px" top="4px" gap="4px">
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        color="#121828"
                        sx={{
                          fontWeight: "500",
                          fontFamily: "Inter",
                          fontSize: "16px",
                        }}
                      >
                        Add your insights and instructions
                      </Typography>
                      {formik.values.generateArticle && (
                        <Tooltip
                          title="When this toggle is on and you leave the field empty, we'll automatically generate instructions using your article outline"
                          placement="top"
                        >
                          <InfoIcon sx={{ fontSize: "16px" }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography
                      color="#7B85AD"
                      sx={{
                        fontWeight: "400",
                        fontFamily: "Inter",
                        fontSize: "12px",
                      }}
                    >
                      Add your instructions and expert insights (data, quotes,
                      personal experience) to guide AI content. Write each
                      instruction or insight on a separate line for clarity.
                    </Typography>
                  </Box>
                  <Box width="100%" marginTop={"10px"}>
                    <InsightsEditor
                      value={formik.values.insights}
                      onChange={(value) =>
                        formik.setFieldValue("insights", value)
                      }
                      placeholder={
                        formik.values.generateArticle
                          ? "Leave empty to auto-generate instructions during article creation, or add your own..."
                          : placeholderText
                      }
                      error={
                        formik.touched.insights &&
                        Boolean(formik.errors.insights)
                      }
                      helperText={
                        formik.touched.insights && formik.errors.insights
                          ? formik.errors.insights
                          : undefined
                      }
                      compact={true}
                      maxLength={20000}
                      showResizeHandle={true}
                      dispatch={dispatch}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
        {!insights && (
          <Box sx={{ pt: 1, textAlign: "right", mt: "auto" }}>
            <Button
              variant="contained"
              type="submit"
              disabled={checkingRanking}
              startIcon={
                checkingRanking ? (
                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                ) : null
              }
            >
              {checkingRanking ? (
                "Checking rankings..."
              ) : formik.values?.generateArticle ? (
                <>
                  {" "}
                  <MagicWandIcon sx={{ mt: 0.6 }} />
                  Generate article
                </>
              ) : (
                "Create brief"
              )}
            </Button>
          </Box>
        )}
      </Box>
      {insights && (
        <Box sx={{ textAlign: "right", mt: "0px !important" }}>
          <Button
            variant="contained"
            type="submit"
            disabled={checkingRanking}
            startIcon={
              checkingRanking ? (
                <CircularProgress size={16} sx={{ color: "inherit" }} />
              ) : null
            }
          >
            {checkingRanking ? (
              "Checking rankings..."
            ) : formik.values?.generateArticle ? (
              <>
                {" "}
                <MagicWandIcon sx={{ mt: 0.6 }} />
                Generate article
              </>
            ) : (
              "Create brief"
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ChooseKeyword;
